import SDKAdapter from '../../core/adapter.js';
import { SDKCollection } from '../../core/collection.js';
import { SDKModel } from '../../core/model.js';
import { SDKModelSchema } from '../../core/schema.js';
import tenantSchema from '../../schemas/tenant.js';
export class TenantError extends Error {
    constructor() {
        super(...arguments);
        this.name = 'TenantError';
    }
}
export class Tenants extends SDKAdapter {
    fetch(query) {
        return this.search(query);
    }
    /** Searches for tenants through inventory api request */
    async search(query) {
        var _a;
        if (!query.systemId)
            throw new Error('Provide systemId to search for tenants');
        const limit = query.limit || Infinity;
        let result = [];
        for (let page = 1; result.length < limit; page++) {
            try {
                const url = `${this.sdk.inventory}/api/inventory/v1/tenants?${query.name ? `name=${query.name}&` : ''}state=Active&systemId=${query.systemId}&labels=Environment%3D${this.sdk.inventoryEnv}&pageSize=100&pageNumber=${page}`;
                if (!query.authorization)
                    await this.sdk.preflight(url, {});
                const tenantResponse = await this.sdk.fetchJSON(url, {
                    headers: {
                        Authorization: query.authorization || `Bearer ${this.sdk.accessToken}`
                    }
                });
                const noProjectIdInQuery = !query.xmProjectId;
                /** Filter tenants with projectId, matching projectId if provided */
                const filteredTenants = tenantResponse.data.filter(function (tenant) {
                    const projectId = TenantModel.generateProjectIdFromTenant(tenant);
                    return projectId && (noProjectIdInQuery || query.xmProjectId === projectId);
                });
                result.push(...filteredTenants);
                if (((_a = tenantResponse === null || tenantResponse === void 0 ? void 0 : tenantResponse.data) === null || _a === void 0 ? void 0 : _a.length) < 100)
                    break;
            }
            catch (e) {
                console.log('Searching for tenants failed', e);
                return null;
            }
        }
        return result.slice(0, limit);
    }
}
export const RegionsMap = {
    'West Europe': 'euw',
    'East US': 'use',
    'West US': 'usw',
    'West US 2': 'usw',
    'Japan East': 'jpe',
    'East Japan': 'jpe',
    'Australia East': 'aue',
    'East Australia': 'aue',
    'Southeast Asia': 'sg',
    eu1: 'euw',
    us1: 'use',
    us2: 'usw',
    jp1: 'jpe',
    au1: 'aue'
};
export class TenantModel extends SDKModel {
    get adapter() {
        return this.sdk.Tenants;
    }
    defineProperties() {
        super.defineProperties();
        this.sdk.utils.defineCollectionAccessor(this, 'datasources', this.constructDatasources(), {}, this.getHiddenProps());
    }
    getHiddenProps() {
        return ['datasources'];
    }
    constructDatasources() {
        const datasources = SDKCollection.construct(this.sdk.Datasource, () => ({
            externalSourceId: this.name,
            type: 'xmTemplate'
        }));
        return datasources;
    }
    constructForms() {
        return SDKCollection.construct(this.sdk.Form, () => ({}));
    }
    getRegionCode() {
        var _a, _b, _c;
        return ((_b = (_a = this.labels) === null || _a === void 0 ? void 0 : _a['RegionCode']) === null || _b === void 0 ? void 0 : _b.toString()) || RegionsMap[(_c = this.labels) === null || _c === void 0 ? void 0 : _c['Region']];
    }
    getProjectId() {
        return TenantModel.generateProjectIdFromTenant(this);
    }
    getProjectName() {
        return TenantModel.generateProjectNameFromTenant(this);
    }
    getURL() {
        return `https://xmc-${this.name}.${this.sdk.sitecorePostfix}`;
    }
    getEnvironmentId() {
        var _a, _b;
        return ((_b = (_a = this.annotations) === null || _a === void 0 ? void 0 : _a['XMCloud.EnvironmentId']) === null || _b === void 0 ? void 0 : _b.toString()) || null;
    }
    getContext() {
        const context = {};
        if (this.name)
            context.tenantName = this.name;
        if (this.organizationId)
            context.organization = this.organizationId;
        if (this.systemId)
            context.systemId = this.systemId;
        return context;
    }
    static generateProjectIdFromTenant(tenant) {
        if (tenant.annotations && tenant.annotations['XMCloud.ProjectId'])
            return tenant.annotations['XMCloud.ProjectId'];
        else if (tenant.name.split('-')[1])
            return `${tenant.organizationId}-${tenant.name.split('-')[1]}`;
        else
            return null;
    }
    static generateProjectNameFromTenant(tenant) {
        return tenant.annotations[Object.keys(tenant.annotations).find((a) => a.match(/project.*name/i))];
    }
    static get schema() {
        return new SDKModelSchema(TenantModel, tenantSchema);
    }
}
//# sourceMappingURL=tenants.js.map